import { autorun, type IReactionDisposer, type IReactionOptions, type IReactionPublic, reaction } from 'mobx';
import { m } from '../mithril';
import type { MithrilVNode } from '../mithril';

export interface IMithrilMountableComponent {
	view: () => MithrilVNode | MithrilVNode[];
}

export interface MithrilLifecycle {
	key?: string;
	oncreate?(vnode: MithrilVNode): void;
	onupdate?(vnode: MithrilVNode): void;
	onremove?(vnode: MithrilVNode): void;
}

export abstract class IMithrilComponent {
	private lifeCycleParams: MithrilLifecycle;

	constructor(key?: string) {
		this.lifeCycleParams = {};
		if (key != null) {
			this.lifeCycleParams.key = key;
		}
		this.lifeCycleParams.oncreate = this.onCreate.bind(this);
		this.lifeCycleParams.onupdate = this.onUpdate.bind(this);
		this.lifeCycleParams.onremove = this.onRemove.bind(this);
	}

	/**
	 * (Mithril)Called after a DOM element is created and attached to the document.
	 * @see https://mithril.js.org/lifecycle-methods.html#oncreate
	 */
	onCreate(_vnode: MithrilVNode): void {}

	/**
	 * (Mithril) Called after a DOM element is updated, while attached to the document.
	 * @see https://mithril.js.org/lifecycle-methods.html#onupdate
	 */
	onUpdate(_vnode: MithrilVNode): void {}

	/**
	 * (Mithril) Called before a DOM element is removed from the document.
	 * @see https://mithril.js.org/lifecycle-methods.html#onremove
	 */
	onRemove(_vnode: MithrilVNode): void {}

	view(): MithrilVNode {
		const nodes = this.nodes;
		if (Array.isArray(nodes)) {
			return m.fragment(this.lifeCycleParams, nodes);
		}
		return m.fragment(this.lifeCycleParams, [nodes]);
	}

	abstract nodes: MithrilVNode | MithrilVNode[];
}

export abstract class IMithrilStateComponent extends IMithrilComponent {
	private reactions: IReactionDisposer[];

	constructor() {
		super();
		this.reactions = [];
	}

	autorun(view: (r: IReactionPublic) => void) {
		this.reactions.push(autorun(view));
	}

	reaction<T>(
		expression: (r: IReactionPublic) => T,
		effect: (arg: T, prev: T | undefined, r: IReactionPublic) => void,
		opts?: IReactionOptions<T, boolean>
	) {
		this.reactions.push(reaction(expression, effect, opts));
	}

	override onRemove() {
		this.reactions.forEach((r) => r());
	}
}
